import { useEffect, useState } from 'react';
import { useToolCatalog } from '@/lib/tool-catalog';
import { NavLink as RouterNavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Clapperboard, Coins, FolderOpen, HelpCircle, LifeBuoy, PanelLeftClose, PanelLeftOpen, Search } from 'lucide-react';
import { useAuth } from '@/contexts/useAuth';
import { openCommandMenu } from '@/components/layout/CommandMenu';
import { SupportDialog } from '@/components/support/SupportDialog';
import { cn } from '@/lib/utils';

// THE DESKTOP LEFT MENU (Studio Blueprint 02/03: "a left rail and command
// menu on desktop"). Every studio with what its first step costs, your work,
// your credits and help, on every page from 1280px wide, under the navbar.
// Words on every row. Phones and tablets get the tab bar and the menu sheet
// instead. Pages leave room for it with body.has-siderail (index.css).

const ROW = 'flex min-h-[40px] items-center gap-2.5 rounded-xl px-2.5 text-[14px] font-medium transition-colors';
const ROW_STATE = ({ isActive }: { isActive: boolean }) =>
  cn(ROW, isActive ? 'bg-primary/10 text-primary' : 'text-white/75 hover:bg-white/[0.05] hover:text-white');

/**
 * `mini` (a tool's own page, owner 2026-10-18: "when you enter a tool the left collum should close so that a new
 * collum can open for the new options and features"): a slim strip of the tools' pictures; its top button opens
 * the whole menu over the page again, and picking anything (or the button again) folds it back.
 */
export function SideRail({ mini = false }: { mini?: boolean }) {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, user } = useAuth();
  const credits = typeof user?.credits === 'number' ? user.credits : null;
  const catalog = useToolCatalog();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  useEffect(() => setOpen(false), [pathname]);

  if (mini && !open) {
    return (
      <aside
        aria-label={t('shell.rail.label')}
        data-testid="side-rail-mini"
        className="fixed bottom-0 left-0 top-16 z-20 hidden w-[var(--rail-mini-w)] flex-col items-center gap-1.5 overflow-y-auto border-r border-white/[0.07] bg-[rgba(8,10,14,0.92)] pb-6 pt-3 [scrollbar-width:none] xl:flex"
      >
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={t('shell.rail.open', { defaultValue: 'Open the menu' })}
          title={t('shell.rail.open', { defaultValue: 'Open the menu' })}
          className="mb-2 flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] text-white/80 hover:border-white/25 hover:text-white"
          data-testid="side-rail-open"
        >
          <PanelLeftOpen className="h-5 w-5" aria-hidden />
        </button>
        {catalog.flatMap((cat) => cat.tools).map((tool) => (
          <RouterNavLink
            key={tool.id}
            to={tool.path}
            title={tool.name}
            aria-label={tool.name}
            // Words under every picture (owner 2026-10-19: "i cant tell what these are suppose to be when its closed like that").
            className={({ isActive }) => cn('flex w-[4.5rem] shrink-0 flex-col items-center gap-1 rounded-xl px-1 py-1.5 transition-colors', isActive ? 'bg-primary/15 ring-1 ring-primary/60' : 'hover:bg-white/[0.06]')}
          >
            <img src={tool.poster} alt="" width={32} height={32} loading="lazy" className="h-8 w-8 rounded-md object-cover" />
            <span className="line-clamp-2 w-full break-words text-center text-[10.5px] font-medium leading-[1.15] text-white/80">{tool.name}</span>
          </RouterNavLink>
        ))}
      </aside>
    );
  }

  return (
    <>
    {mini && <button type="button" aria-hidden tabIndex={-1} onClick={() => setOpen(false)} className="fixed inset-0 top-16 z-30 hidden cursor-default bg-black/40 xl:block" />}
    <aside
      aria-label={t('shell.rail.label')}
      data-testid="side-rail"
      className={cn('fixed bottom-0 left-0 top-16 hidden w-[var(--rail-w)] flex-col overflow-y-auto border-r border-white/[0.07] bg-[rgba(8,10,14,0.92)] px-3 pb-6 pt-4 [scrollbar-width:thin] xl:flex',
        mini ? 'z-40 bg-[rgba(8,10,14,0.98)] shadow-[20px_0_60px_rgba(0,0,0,0.6)]' : 'z-20')}
    >
      {mini && (
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="mb-3 flex min-h-[40px] items-center gap-2 rounded-xl px-2.5 text-[13.5px] text-white/75 hover:bg-white/[0.05] hover:text-white"
        >
          <PanelLeftClose className="h-4 w-4" aria-hidden /> {t('shell.rail.close', { defaultValue: 'Close the menu' })}
        </button>
      )}
      <button
        type="button"
        onClick={openCommandMenu}
        className="mb-4 flex min-h-[40px] items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-3 text-left text-[13.5px] text-muted-foreground hover:border-white/20"
      >
        <Search className="h-4 w-4 text-primary" aria-hidden />
        <span className="flex-1">{t('shell.search')}</span>
        <kbd className="rounded border border-white/15 px-1.5 font-mono text-[10px]">Ctrl K</kbd>
      </button>

      {/* Every tool, in the category it belongs to (owner 2026-10-16: no
          separate "quick tools" folder). A tool that cannot render right now
          still opens: only its paid button is greyed, on its own page. */}
      {catalog.map((cat, ci) => (
        <div key={cat.key} className={ci ? 'mt-4' : ''}>
          <p className="px-2.5 pb-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">{t(`toolCats.${cat.key}`)}</p>
          <ul className="space-y-0.5">
            {cat.tools.map((tool) => (
              <li key={tool.id}>
                <RouterNavLink to={tool.path} className={ROW_STATE} data-testid={`rail-${tool.studio ?? tool.id}`}>
                  <img src={tool.poster} alt="" width={24} height={24} loading="lazy" className="h-6 w-6 shrink-0 rounded-md object-cover" />
                  <span className="min-w-0 flex-1 truncate">{tool.name}</span>
                  <span className={cn('shrink-0 text-[11px] font-semibold', tool.free ? 'text-accent' : 'text-primary')}>{tool.shortPrice}</span>
                </RouterNavLink>
              </li>
            ))}
          </ul>
        </div>
      ))}

      <p className="mt-5 px-2.5 pb-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">{t('shell.rail.work')}</p>
      <ul className="space-y-0.5">
        <li>
          <RouterNavLink to="/projects" className={ROW_STATE}>
            <FolderOpen className="h-4 w-4 shrink-0" aria-hidden /> {t('shell.tabs.projects')}
          </RouterNavLink>
        </li>
        <li>
          <RouterNavLink to="/watch" className={ROW_STATE}>
            <Clapperboard className="h-4 w-4 shrink-0" aria-hidden /> {t('shell.tabs.watch')}
          </RouterNavLink>
        </li>
      </ul>

      <p className="mt-5 px-2.5 pb-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">{t('shell.tabs.credits')}</p>
      <ul className="space-y-0.5">
        {isAuthenticated && (
          <li>
            <RouterNavLink to="/credits-history" className={ROW_STATE}>
              <Coins className="h-4 w-4 shrink-0 text-primary" aria-hidden />
              <span className="flex-1">{t('shell.rail.balance')}</span>
              <span className="tabular-nums text-foreground">{credits === null ? '—' : credits.toLocaleString()}</span>
            </RouterNavLink>
          </li>
        )}
        <li>
          <RouterNavLink to="/pricing" className={ROW_STATE}>
            <Coins className="h-4 w-4 shrink-0" aria-hidden /> {t('shell.rail.buy')}
          </RouterNavLink>
        </li>
      </ul>

      <p className="mt-5 px-2.5 pb-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">{t('shell.rail.help')}</p>
      <ul className="space-y-0.5">
        <li>
          <SupportDialog
            trigger={
              <button type="button" className={cn(ROW, 'w-full text-white/75 hover:bg-white/[0.05] hover:text-white')}>
                <LifeBuoy className="h-4 w-4 shrink-0" aria-hidden /> {t('support.title')}
              </button>
            }
          />
        </li>
        <li>
          <RouterNavLink to={{ pathname: '/', hash: '#faq' }} className={() => ROW_STATE({ isActive: false })}>
            <HelpCircle className="h-4 w-4 shrink-0" aria-hidden /> {t('navigation.faq')}
          </RouterNavLink>
        </li>
      </ul>
    </aside>
    </>
  );
}
